<template>
    <OnboardingErrorView v-if="run.apiError.value || scanFailed" embedded :show-continue="scanFailed" @retry="handleRetry" @continue="handleContinueAfterFailure" />

    <ProfileNetworkStep
        v-else-if="showNetwork"
        :invite-url="inviteUrl"
        :busy="continuing"
        :published="published"
        :calendar-last="setup.calendarLast && calendarActionable"
        @continue="handleNetworkContinue" />

    <IdentityStep
        v-else-if="view === 'identity'"
        embedded
        :prefill="run.run.value.identity_prefill"
        :busy="run.busy.value"
        :initial-manages-people="run.run.value.manages_people"
        returning
        @submit="handleIdentity" />

    <IntroStep v-else-if="view === 'intro'" embedded :first-name="run.firstName.value" :manages-people="run.managesPeople.value" @complete="showNextScreen()" />

    <ContextRingsStep v-else-if="view === 'context_rings'" embedded :context="setup.context.value" @continue="showNextScreen()" />

    <CalendarConnectStep
        v-else-if="view === 'calendar_connect'"
        embedded
        :calendar="run.run.value.calendar"
        :organization-name="run.run.value.organization_name"
        :busy="run.busy.value"
        :calendar-last="setup.calendarLast"
        :personalized="otherAsksDone"
        @connect="run.connectCalendar"
        @continue="showNextScreen()"
        @skip="handleSkipCalendar" />

    <ThinkingScreen
        v-else-if="view === 'scanning'"
        embedded
        v-bind="THINKING_SCREENS.scan"
        :label="run.scanLabel.value"
        :show-continue="scanStalled"
        @continue="showNextScreen()" />

    <CalendarPayoffStep
        v-else-if="view === 'calendar_payoff'"
        embedded
        :stats="run.scanStats.value"
        :readings="run.scanReadings.value"
        :readings-settled="run.readingsSettled.value"
        location="guided_setup"
        @continue="showNextScreen()" />

    <AssessmentIntroStep
        v-else-if="view === 'assessment_intro'"
        embedded
        :stats="run.scanStats.value"
        :manages-people="run.managesPeople.value"
        :question-count="run.run.value.assessment_question_count"
        :calendar-skipped="!calendarConnected || setup.calendarLast"
        :calendar-last="setup.calendarLast"
        @continue="showNextScreen()"
        @skip="handleSkipAssessment" />

    <AssessmentStep
        v-else-if="view === 'assessment'"
        embedded
        :assessment="run.assessment.value"
        :busy="run.busy.value"
        @submitted="handleAssessmentComplete"
        @skip="handleSkipAssessment" />

    <!-- The wheel holds its spin until the archetype arrives, then plays its landing; the reveal waits for
         that so the coloured core it ends on becomes this screen's medallion. A reopened ask skips the
         wheel outright: there is nothing left to reveal to someone who has already seen their type. -->
    <ThinkingScreen
        v-else-if="view === 'perspective_result' && !perspectiveLanded && !startedAtResult"
        embedded
        v-bind="THINKING_SCREENS.perspective"
        :land="run.perspectiveResult.value?.letters ?? null"
        @landed="perspectiveLanded = true" />

    <PerspectiveResultStep
        v-else-if="view === 'perspective_result' && run.perspectiveResult.value !== null"
        embedded
        :result="run.perspectiveResult.value!"
        :calendar-connected="calendarConnected && !setup.calendarLast"
        :calendar-last="setup.calendarLast"
        :retakeable="startedAtResult"
        @continue="showNextScreen()"
        @retake="handleRetake" />

    <!-- The first reading to land ends the thinking screen; the readings step
         fills the remaining slots in place as the rest arrive. -->
    <ThinkingScreen
        v-else-if="view === 'sharpened' && (sharpenedSkipped || (!run.sharpenedSettled.value && !run.hasSharpenedReadings.value))"
        embedded
        v-bind="THINKING_SCREENS.readings"
        :show-continue="run.sharpenedPhase.outcome.value === 'timed_out' || run.sharpenedPhase.outcome.value === 'error'"
        @continue="showNextScreen()" />

    <SharpenedReadingsStep
        v-else-if="view === 'sharpened'"
        embedded
        :readings="run.sharpenedReadings.value"
        :settled="run.sharpenedSettled.value"
        :available-access-levels="run.run.value.available_access_levels"
        :calendar-connected="calendarConnected"
        location="guided_setup"
        @continue="showNextScreen()" />

    <ProfileIntroStep
        v-else-if="view === 'profile_intro'"
        embedded
        :first-name="run.run.value.identity_prefill.first_name"
        :manages-people="run.managesPeople.value"
        :busy="run.busy.value"
        :draft-ready="run.run.value.profile_status === 'ready'"
        @continue="showNextScreen()"
        @skip="showNextScreen()" />

    <ThinkingScreen
        v-else-if="view === 'profile' && run.profileGenerating.value"
        embedded
        v-bind="THINKING_SCREENS.profile"
        :show-continue="profileStalled"
        @continue="showNextScreen()" />

    <ProfilePublishStep
        v-else-if="view === 'profile'"
        embedded
        :preview="run.profilePreview.value"
        :available-access-levels="run.run.value.available_access_levels"
        :profile-sharer-names="run.scanStats.value?.profile_sharer_names ?? []"
        :generation-failed="run.profilePreview.value.status === 'failed'"
        :busy="run.busy.value"
        :calendar-last="setup.calendarLast"
        @publish="handlePublish"
        @saved="run.setProfileSubtopicContent"
        @skip="showNextScreen()" />
</template>

<script setup lang="ts">
/**
 * Do not call /advance/ here: it requires the server cursor to match the departing step, which out-of-order
 * setup cannot guarantee. Screen transitions stay local.
 */
import AssessmentIntroStep from "~vue/CalendarFirstOnboarding/AssessmentIntroStep.vue";
import AssessmentStep from "~vue/CalendarFirstOnboarding/AssessmentStep.vue";
import CalendarConnectStep from "~vue/CalendarFirstOnboarding/CalendarConnectStep.vue";
import CalendarPayoffStep from "~vue/CalendarFirstOnboarding/CalendarPayoffStep.vue";
import ContextRingsStep from "~vue/CalendarFirstOnboarding/ContextRingsStep.vue";
import IdentityStep from "~vue/CalendarFirstOnboarding/IdentityStep.vue";
import IntroStep from "~vue/CalendarFirstOnboarding/IntroStep.vue";
import OnboardingErrorView from "~vue/CalendarFirstOnboarding/OnboardingErrorView.vue";
import PerspectiveResultStep from "~vue/CalendarFirstOnboarding/PerspectiveResultStep.vue";
import ProfileIntroStep from "~vue/CalendarFirstOnboarding/ProfileIntroStep.vue";
import ProfilePublishStep from "~vue/CalendarFirstOnboarding/ProfilePublishStep.vue";
import { type ScreenPhase, screenStatusFor } from "~vue/CalendarFirstOnboarding/screenStatus";
import SharpenedReadingsStep from "~vue/CalendarFirstOnboarding/SharpenedReadingsStep.vue";
import ThinkingScreen from "~vue/CalendarFirstOnboarding/ThinkingScreen.vue";
import { THINKING_SCREENS } from "~vue/CalendarFirstOnboarding/thinkingScreens";
import type { AccessLevel } from "~vue/CalendarFirstOnboarding/types";
import type { IdentitySubmission } from "~vue/CalendarFirstOnboarding/useOnboardingRun";
import ProfileNetworkStep from "~vue/ProfileSetup/ProfileNetworkStep.vue";
import { logAskEvent } from "~vue/ProfileSetup/setupAnalytics";
import type { SetupStepId } from "~vue/ProfileSetup/types";
import { type ProfileSetup, screensFor, type SetupScreen } from "~vue/ProfileSetup/useProfileSetup";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { setup, stepId, inviteUrl, directoryEnabled } = defineProps<{
    setup: ProfileSetup;
    stepId: SetupStepId;
    inviteUrl: string;
    directoryEnabled: boolean;
}>();

/*
 * These screens carry no live markup of their own, so the page's single region has to be told what is on
 * screen. Section names alone leave every swap inside a section silent, waits included.
 */
const emit = defineEmits<{ scrollReset: []; statusChange: [status: string | null]; immersiveChange: [immersive: boolean] }>();

const run = setup.run;

/* A connected calendar the user then skipped is not one the readings can lean on. */
const calendarConnected = computed(() => run.run.value.calendar.connected && !run.run.value.calendar_skipped);

/* Calendar-last only sends someone on to Calendar when it can still produce insights for them. */
const calendarActionable = computed(() => !run.run.value.calendar_skipped && (run.run.value.calendar.connected || run.run.value.calendar.provider !== ""));

/* Calendar-last pitches the combined insights only once there is a Perspective and profile to combine. */
const otherAsksDone = computed(() => setup.steps.value.every((step) => step.id === "calendar" || step.done));

const screens = ref<SetupScreen[]>([]);
const view = ref<SetupScreen | null>(null);
const showNetwork = ref(false);
const published = ref(false);
const continuing = ref(false);
let disposed = false;
onBeforeUnmount(() => {
    disposed = true;
});

function isCurrent(): boolean {
    return !disposed && setup.activeStep.value === stepId;
}

async function showScreen(next: SetupScreen | null): Promise<void> {
    if (!isCurrent()) {
        return;
    }
    if (next === null) {
        await continueAfter(setup.activeStep.value);
        return;
    }
    view.value = next;
    emit("scrollReset");
    if ((await run.enterStep(next)) !== "entered") {
        await showNextScreen();
    }
}

async function showNextScreen(): Promise<void> {
    const index = view.value === null ? -1 : screens.value.indexOf(view.value);
    await showScreen(screens.value[index + 1] ?? null);
}

/* A finished ask reopens on what it produced, not on the screen that asks for it again: a connected calendar
   on its scan, an answered assessment on its archetype, a written profile on the draft itself.
   The profile keeps its intro until a draft exists, because that screen's action is what consents to
   generation overwriting whatever the user already wrote. */
function firstScreen(list: SetupScreen[]): SetupScreen | null {
    /* Calendar-last opens a connected calendar on its "Connected" screen, so the analysis starts on request. */
    if (stepId === "calendar" && run.run.value.calendar.connected && !setup.calendarLast) {
        return "scanning";
    }
    if (stepId === "perspective" && run.run.value.has_perspective_result) {
        return "perspective_result";
    }
    if (stepId === "publish" && run.run.value.profile_status === "ready") {
        return "profile";
    }
    return list[0] ?? null;
}

const scanFailed = computed(() => view.value === "sharpened" && run.sharpenedReadings.value?.status === "failed" && run.run.value.scan_status === "failed");
const sharpenedSkipped = computed(() => run.sharpenedPhase.outcome.value === "resolved" && run.sharpenedReadings.value?.status === "skipped");

/*
 * A wait that has outrun its pipeline and now offers a way past it. The screens read these to show their
 * escape hatch, so the announcement cannot drift from the button appearing. The readings wait needs no entry
 * because its own timeout settles it and swaps in the result screen.
 */
const scanStalled = computed(() => run.scanPhase.outcome.value === "timed_out" || run.scanPhase.outcome.value === "error");
const profileStalled = computed(() => run.profilePhase.outcome.value === "timed_out");

/* Mirrors the template's branches, so a wait and the result that replaces it announce separately. */
const screenPhase = computed<ScreenPhase>(() => {
    if (run.apiError.value || scanFailed.value) {
        return "error";
    }
    if (view.value === "scanning") {
        return scanStalled.value ? "stalled" : "waiting";
    }
    if (view.value === "assessment" && run.assessment.value.session_id === "") {
        return "waiting";
    }
    if (view.value === "perspective_result" && run.perspectiveResult.value === null) {
        return "waiting";
    }
    if (view.value === "sharpened" && (sharpenedSkipped.value || (!run.sharpenedSettled.value && !run.hasSharpenedReadings.value))) {
        return "waiting";
    }
    if (view.value === "profile" && run.profileGenerating.value) {
        return profileStalled.value ? "stalled" : "waiting";
    }
    return "result";
});

watch(
    () => (showNetwork.value ? t("onboarding.profileSetup.networkStep.title") : view.value === null ? null : screenStatusFor(view.value, screenPhase.value)),
    (status, previous) => {
        /* Opening an ask already announces the ask's own name. Only movement inside it is news, which is
           where waits and their results live. */
        if (previous === null || previous === undefined) {
            return;
        }
        emit("statusChange", status);
    },
);

/* Calendar-last's scan hands off to the same loader on the overview, so it drops the rail the overview lacks. */
watch(
    () => setup.calendarLast && view.value === "scanning" && !run.apiError.value,
    (immersive) => emit("immersiveChange", immersive),
    { immediate: true },
);

/* Only the reopened result screen offers a retake; mid-flow it is the reveal of answers just given. */
const startedAtResult = ref(false);

/* Set by the wheel when its landing has finished playing, and cleared whenever the step is re-entered,
   which is where `enterStep` drops the result it is about to re-fetch. */
const perspectiveLanded = ref(false);

watch(run.perspectiveResult, (result) => {
    if (result === null) {
        perspectiveLanded.value = false;
    }
});

async function handleRetake(): Promise<void> {
    startedAtResult.value = false;
    await showScreen("assessment_intro");
}

/* Each section owns its runner. Watching the global active section here would enter
   the next ask once before unmounting and again when its own runner mounts. */
watch(
    () => stepId,
    async (id) => {
        logAskEvent(AmplitudeEventNames.GUIDED_SETUP_ASK_VIEWED, id, setup.askProgress(id));
        screens.value = screensFor(id, {
            hasPerspectiveResult: run.run.value.has_perspective_result,
            calendarConnected: calendarConnected.value,
            calendarLast: setup.calendarLast,
        });
        const first = setup.entryScreen.value ?? firstScreen(screens.value);
        startedAtResult.value = first === "perspective_result";
        await showScreen(first);
    },
    { immediate: true },
);

watch(
    () => run.scanPhase.outcome.value,
    async (outcome) => {
        if (view.value === "scanning" && outcome === "resolved" && run.scanPhase.data.value?.status === "stats_ready") {
            await setup.refreshState();
            await showNextScreen();
        }
    },
);

watch(
    () => run.sharpenedReadings.value?.status,
    async (status) => {
        if (status === "failed") {
            await setup.refreshState();
        }
    },
);

watch(
    () => view.value === "sharpened" && sharpenedSkipped.value,
    async (skipped) => {
        if (skipped) {
            await showNextScreen();
        }
    },
);

async function continueAfter(ask: SetupStepId | null): Promise<void> {
    if (!isCurrent()) {
        return;
    }
    await setup.refreshState();
    if (!isCurrent()) {
        return;
    }
    /* Calendar-last closes on the calendar, whose analysis ends on the overview's insights. */
    if (setup.calendarLast && ask === "publish" && calendarActionable.value) {
        setup.openStep("calendar", calendarConnected.value ? "scanning" : null);
        return;
    }
    const analyzed = setup.calendarLast && ask === "calendar" && !run.run.value.calendar_skipped;
    const candidate = analyzed ? null : setup.nextUnfinished(ask ?? undefined);
    const next = setup.calendarLast && candidate === "calendar" && !calendarActionable.value ? null : candidate;
    if (next === null) {
        await setup.finish();
        if (isCurrent()) {
            await setup.closeStep();
        }
        return;
    }
    /* Moving on through the flow introduces the profile even when the background run already wrote it; only a
       reopened ask jumps straight to the draft. */
    setup.openStep(next, next === "publish" ? "profile_intro" : null);
}

async function handleIdentity(payload: IdentitySubmission): Promise<void> {
    if (!(await run.submitIdentity(payload))) {
        return;
    }
    await setup.refreshState();
    await showNextScreen();
}

/* Stay on this ask if saving the skip fails; otherwise the error's retry would target the next ask. */
async function handleSkipCalendar(): Promise<void> {
    if (!(await run.skipCalendar())) {
        return;
    }
    await continueAfter("calendar");
}

async function handleAssessmentComplete(): Promise<void> {
    if (!(await run.completeAssessment())) {
        return;
    }
    /*
     * Refresh the rail after answers persist so the assessment shows complete while its result screens are
     * still open.
     */
    await setup.refreshState();
    await showNextScreen();
}

async function handleSkipAssessment(): Promise<void> {
    if (!(await run.skipAssessment())) {
        return;
    }
    await continueAfter("perspective");
}

async function handlePublish(access: AccessLevel): Promise<void> {
    if (!(await run.publishProfile(access))) {
        return;
    }
    /* Calendar-last offers coworkers after either choice; only the published one gets the chip. */
    if (!directoryEnabled || (access === "private" && !setup.calendarLast)) {
        await continueAfter("publish");
        return;
    }
    await setup.refreshState();
    if (isCurrent()) {
        published.value = access !== "private";
        showNetwork.value = true;
        emit("scrollReset");
    }
}

async function handleNetworkContinue(): Promise<void> {
    if (continuing.value) {
        return;
    }
    continuing.value = true;
    try {
        await continueAfter("publish");
    } finally {
        continuing.value = false;
    }
}

async function handleContinueAfterFailure(): Promise<void> {
    run.cancelScanStart();
    run.apiError.value = false;
    await showNextScreen();
}

async function handleRetry(): Promise<void> {
    run.apiError.value = false;
    if (scanFailed.value && !(await run.startScan())) {
        return;
    }
    if (view.value !== null) {
        await showScreen(view.value);
    }
}
</script>
